import { K8sProviderIconStatus } from '@cast/features-cluster-ui';
import { useMemo, useState, MouseEvent } from 'react';

import { Popover, useTheme } from '@mui/material';
import { generatePath, matchPath, useLocation } from 'react-router-dom';

import {
  List,
  ListItem,
  ListItemText,
  ListSearchInput,
  Scroller,
  Icons,
  DsShadow,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { useClusters } from '@cast/shared-cluster-context-data-access';
import { Cluster } from '@cast/types';
import { useWindowWidth } from '@cast/utils';

import { partitionNotFoundClusters } from 'components/cluster/not-found-clusters';
import { orderClustersForDashboardList } from 'features/dashboard/utils';
import { useIsOrganizationReadOnly } from 'hooks/useIsOrganizationReadOnly';
import { useNavigate } from '@cast/infrastructure-organization';
import { POST_PLG_LAYOUT_MAX_WIDTH } from 'main-layout-op/_constants';

import { useSidebarCluster } from '../../SidebarClusterProvider';
import { nestedPaths, premiumFeaturePaths } from '@cast/console-constants';

type BuildLinkArgs = {
  location: ReturnType<typeof useLocation>;
  activeId?: string;
  cluster: Cluster;
  defaultPathTemplate: string;
};

const buildLink = ({
  location,
  activeId,
  cluster,
  defaultPathTemplate,
}: BuildLinkArgs) => {
  const isPremiumPath = premiumFeaturePaths.some((path) => {
    return matchPath(path, location.pathname);
  });

  const isNestedPath = nestedPaths.some((path) => {
    return matchPath(path, location.pathname);
  });

  if (!activeId || (isPremiumPath && !cluster.credentialsId) || isNestedPath) {
    return generatePath(defaultPathTemplate, { clusterId: cluster.id });
  }

  if (location.pathname.includes(activeId)) {
    return location.pathname.replace(activeId, cluster.id);
  }

  return generatePath(defaultPathTemplate, { clusterId: cluster.id });
};

type Props = {
  defaultPathTemplate: string;
};

export const SidebarClusterSelect = (props: Props) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOrganizationReadOnly = useIsOrganizationReadOnly();
  // OP demo must allow selecting the synthetic demo cluster for navigation.
  const canSelectCluster = !isOrganizationReadOnly;
  const isPopoverOpen = Boolean(anchorEl);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const windowWidth = useWindowWidth();
  const menuWidth = anchorEl?.getBoundingClientRect().width;

  const currentCluster = useSidebarCluster();
  const { clusters } = useClusters({
    polling: false,
    enabled: Boolean(currentCluster?.id),
  });

  const currentClusterId = currentCluster?.id;
  const clusterOptions = useMemo(() => {
    const { activeClusters, notFoundClusters } =
      partitionNotFoundClusters(clusters);

    return [
      ...orderClustersForDashboardList(activeClusters),
      ...notFoundClusters,
    ].map((cluster) => ({
      cluster,
      selected: cluster.id === currentClusterId,
    }));
  }, [clusters, currentClusterId]);

  const handleOpen = (event: MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const closePopover = () => setAnchorEl(null);

  const handleClusterSelect = (
    event: MouseEvent<HTMLElement>,
    selectedCluster: Cluster
  ) => {
    closePopover();
    event.stopPropagation();

    navigate(
      buildLink({
        location,
        activeId: currentClusterId,
        cluster: selectedCluster,
        defaultPathTemplate: props.defaultPathTemplate,
      })
    );
  };

  const selectedOption = clusterOptions.find((item) => item.selected);

  const filteredData = clusterOptions.filter(
    ({ cluster }) =>
      cluster.name.includes(searchInputValue) ||
      cluster.providerNamespaceId?.includes(searchInputValue) ||
      cluster.id.includes(searchInputValue)
  );

  return (
    <>
      <Box
        alignItems="center"
        borderRadius="4px"
        bgcolor="#fff"
        height="36px"
        marginBottom={8}
        padding="0px 8px 0px 12px"
        onClick={canSelectCluster ? handleOpen : undefined}
        display="flex"
        justifyContent="space-between"
        sx={{
          border: tokens.theme.border.structural.neutral,
          ...(isPopoverOpen && {
            border: tokens.theme.border.status.info,
            boxShadow: theme.shadows[DsShadow.ACTIVE_GLOW],
          }),
          '&:hover': canSelectCluster
            ? {
                cursor: 'pointer',
              }
            : {},
        }}
        data-testid="cluster-chooser-trigger"
      >
        <Stack
          direction="row"
          gap="8px"
          width="calc(100% - 16px)"
          alignItems="center"
        >
          {selectedOption ? (
            <>
              <K8sProviderIconStatus
                cluster={selectedOption.cluster}
                size={14}
              />
              <Stack overflow="hidden" justifyContent="center">
                <Typography variant="P12M" lineHeight="16px" noWrap>
                  {selectedOption.cluster.name}
                </Typography>
                {selectedOption.cluster.providerNamespaceId && (
                  <Typography
                    variant="P10R"
                    lineHeight="12px"
                    color="textSecondary"
                    noWrap
                  >
                    {selectedOption.cluster.providerNamespaceId}
                  </Typography>
                )}
              </Stack>
            </>
          ) : (
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
              noWrap
            >
              -Select cluster-
            </Typography>
          )}
        </Stack>

        <Icons.WithRotation rotated={!isPopoverOpen}>
          <Icons.CaretUp size={16} color="var(--grey-500)" />
        </Icons.WithRotation>
      </Box>
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          marginTop: '4px',
          marginLeft: windowWidth < POST_PLG_LAYOUT_MAX_WIDTH ? '-8px' : '0px',
          padding: '4px 0px',
        }}
        disableScrollLock
      >
        <Box width={menuWidth}>
          <ListSearchInput
            autoFocus
            autoComplete="off"
            size="small"
            placeholder="Enter cluster name"
            listItemProps={{
              sx: { '&.Mui-focusVisible': { background: 'none' } },
            }}
            value={searchInputValue}
            onChange={(_, v) => setSearchInputValue(v)}
            showClearButton
            clearButtonProps={{
              onClick: () => setSearchInputValue(''),
            }}
          />

          <List size="medium" disablePadding testId="cluster-chooser-list">
            {filteredData.length ? (
              <Scroller
                sx={{ gap: '2px', overflow: 'auto', maxHeight: '225px' }}
              >
                {filteredData.map((option) => {
                  return (
                    <ListItem
                      key={option.cluster.id}
                      onClick={(it) => handleClusterSelect(it, option.cluster)}
                      selected={option.selected}
                      startAdornment={
                        <K8sProviderIconStatus
                          cluster={option.cluster}
                          size={14}
                        />
                      }
                      startAdornmentSx={{
                        alignSelf: 'flex-start',
                        marginTop: '2px',
                      }}
                      multiline
                    >
                      <ListItemText
                        primary={option.cluster.name}
                        primaryTypographyProps={{ variant: 'P12M' }}
                        secondary={option.cluster.providerNamespaceId}
                      />
                    </ListItem>
                  );
                })}
              </Scroller>
            ) : (
              <ListItem
                startAdornment={<Icons.Nut size={14} />}
                startAdornmentSx={{
                  alignSelf: 'flex-start',
                  marginTop: '2px',
                }}
                multiline
              >
                <ListItemText
                  primary="No clusters found"
                  secondary="Adjust your search criteria"
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};
